import { useState } from 'react'
import { useImageStatus } from './useImageStatus'

/**
 * Relación de aspecto del lockup (`viewBox` de
 * `public/brand/versports-lockup-*.svg`). La usa la caja placeholder para
 * ocupar exactamente el mismo lugar que el logo mientras carga: sin esto, el
 * layout salta cuando la imagen aparece. La variante sin tagline recorta el
 * viewBox a la baseline del wordmark (639.2 → 576.1), así que tiene su propia
 * relación.
 */
const LOCKUP_ASPECT = '1057 / 639.2'
const LOCKUP_ASPECT_NO_TAGLINE = '1057 / 576.1'

/**
 * Lockup de VerSports.
 *
 * Invariante (bug mg 2026-09-07, "sigo viendo imagen rota en transiciones, ej.
 * al actualizar en /vr/"): nunca se ve el glifo de imagen rota del browser ni
 * el texto "VerSports" parpadeando en una carga normal. Por eso:
 *
 * - El asset se precarga fuera del árbol (`useImageStatus`, que reintenta con
 *   backoff); el `<img>` visible se monta recién cuando terminó de cargar.
 * - Mientras carga se pinta una caja vacía del mismo tamaño — nada de texto,
 *   que es lo que se veía flashear.
 * - Recién con el fallo definitivo (tras los reintentos) se degrada al texto
 *   "VerSports".
 * - El `<img>` va con `alt=""` y el nombre accesible lo pone el wrapper
 *   (`role="img"` + `aria-label`), así que un `alt` no vacío no puede pintar
 *   el glifo en ningún caso intermedio.
 *
 * `className` y el tamaño (`size`) se aplican al wrapper; el `<img>` lo llena.
 *
 * `tagline={false}` sirve el lockup sin la línea "PREPARE FOR PRESSURE"
 * (`versports-lockup-*-notagline.svg`), para cabeceras que ponen el claim
 * como texto aparte — hoy el lobby del vr-app.
 */
export function VerSportsLogo({
  className = '',
  size = 'sm',
  tone = 'light',
  assetBase = '/brand',
  tagline = true,
}: {
  className?: string
  size?: 'sm' | 'lg'
  tone?: 'light' | 'dark'
  assetBase?: string
  tagline?: boolean
}) {
  const sizeCls = size === 'lg' ? 'h-32 w-auto' : 'h-12 w-auto'
  const variant = tagline ? '' : '-notagline'
  // Full lockup uses the original brand artwork, not the reconstructed SVG
  // wordmark. Cropped no-tagline headers retain their dedicated asset.
  const file = tagline ? `lockup-${tone === 'dark' ? 'dark' : 'white'}.png` :
    tone === 'dark'
      ? `versports-lockup-dark${variant}.svg`
      : `versports-lockup-white${variant}.svg`
  const src = `${assetBase}/${file}`

  const status = useImageStatus(src)
  // Red de contención: si el `<img>` ya montado falla igual (una revalidación
  // que se cae después de la precarga), se vuelve al texto en vez de dejar un
  // hueco. Anclado al `src` para que cambiar `tone`/`assetBase` arranque limpio.
  const [visibleFailed, setVisibleFailed] = useState<string | null>(null)
  const failed = status === 'failed' || visibleFailed === src

  return (
    <span
      role="img"
      aria-label="VerSports"
      className={`${sizeCls} ${className}`}
      style={{ display: 'inline-flex', alignItems: 'center' }}
    >
      {status === 'loaded' && !failed ? (
        <img
          src={src}
          alt=""
          decoding="async"
          style={{ height: '100%', width: 'auto' }}
          onError={() => setVisibleFailed(src)}
        />
      ) : failed ? (
        <span style={{ fontWeight: 700, letterSpacing: '0.06em', fontSize: '1.1em' }}>
          VerSports
        </span>
      ) : (
        <span aria-hidden="true" style={{ height: '100%', aspectRatio: tagline ? LOCKUP_ASPECT : LOCKUP_ASPECT_NO_TAGLINE }} />
      )}
    </span>
  )
}
